import * as React from "react";
import { useState, useCallback, useRef, useEffect, forwardRef } from "react";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import { createBlurHandler, getAllFocusableElements } from "./utils";
import type { CompoundPopoverProps, PopoverProps } from "./interfaces";
import {
  PopoverContextProvider,
  PopoverContextConsumer,
  usePopoverContext,
} from "./popover-context";
import usePopoverInteraction from "./use-popover-interaction";
import Button from "./popover-button";
import Panel from "./popover-panel";

const baseClassName = "eb-popover";

const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      children,
      className: consumerClassName,
      panelPlacement = "top-end",
      onToggle,
      uniqueName,
      elementToFocus = "panel",
      ...rest
    }: PopoverProps,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const getBlocksWith = createBemBlockBuilder([
      baseClassName,
      consumerClassName,
    ]);

    const isButtonClickableRef = useRef(true);
    const panelRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const shouldSetFocusButtonOnClose = useRef(false);

    const { getUserInteraction } = usePopoverInteraction(
      panelRef,
      `.${baseClassName}`,
    );

    const togglePopover = useCallback(() => {
      onToggle && onToggle(!isOpen);

      setIsOpen(!isOpen);
    }, [isOpen, onToggle]);

    useEffect(() => {
      if (isOpen && panelRef.current) {
        if (elementToFocus === "panel") {
          (panelRef.current as HTMLDivElement).focus();
        }

        if (elementToFocus === "first-focusable") {
          const firstFocusableElement = getAllFocusableElements(
            panelRef.current,
          )[0];

          (firstFocusableElement as HTMLElement).focus();
        }

        return;
      }

      if (shouldSetFocusButtonOnClose.current && buttonRef.current) {
        (buttonRef.current as HTMLButtonElement).focus();
      }

      shouldSetFocusButtonOnClose.current = true;

      setTimeout(() => {
        isButtonClickableRef.current = true;
      }, 200);
    }, [elementToFocus, isOpen]);

    const handleOnClick = () => {
      // ignore click when button was closed by panel blur event
      if (isButtonClickableRef.current) {
        togglePopover();
      }
    };

    const handleOnKeyPress = (event: React.KeyboardEvent) => {
      if (isOpen && event.key === "Escape") {
        event.preventDefault();
        togglePopover();
      }
    };

    const handleOnOutsideInteractions = useCallback(
      (event: React.FocusEvent<HTMLElement>) => {
        if (!isOpen) {
          return;
        }

        if (event.relatedTarget === buttonRef.current) {
          isButtonClickableRef.current = false;
        }

        togglePopover();

        const userInteraction = getUserInteraction(event.relatedTarget);

        // When the blur event is firing with a relatedTarget, the user has interacted with
        // either the current panelRef, another known popover, or an element outside any known popovers.
        shouldSetFocusButtonOnClose.current =
          !userInteraction?.withinAnotherPopover ?? false;
      },
      [getUserInteraction, isOpen, togglePopover],
    );

    const handleOnPanelBlur = createBlurHandler(
      panelRef,
      handleOnOutsideInteractions,
    );

    const closePopover = () => {
      setIsOpen(false);
    };

    const popoverProps = {
      handleOnClick,
      getBlocksWith,
      isOpen,
      panelPlacement,
      panelRef,
      buttonRef,
      handleOnPanelBlur,
      uniqueName,
      closePopover,
    };

    return (
      <PopoverContextProvider value={popoverProps}>
        <div
          ref={ref}
          className={getBlocksWith()}
          onKeyDown={handleOnKeyPress}
          {...rest}
        >
          {children}
        </div>
      </PopoverContextProvider>
    );
  },
) as CompoundPopoverProps;

Popover.Button = Button;
Popover.Button.displayName = "Popover.Button";
Popover.Panel = Panel;
Popover.Panel.displayName = "Popover.Panel";
Popover.ContextConsumer = PopoverContextConsumer;
Popover.ContextConsumer.displayName = "Popover.ContextConsumer";
Popover.useContext = usePopoverContext;
Popover.displayName = "Popover";

export default Popover;
